/* eslint-disable jsx-a11y/anchor-is-valid */
import {FormEvent, useEffect, useRef, useState} from 'react'
import clsx from 'clsx'
import {EnrollmentData} from '../mfa/loginFlow'
import {isCompleteTotp, sanitizeTotpInput, TOTP_LENGTH} from '../mfa/mfaCodes'
import {copyToClipboard} from '../mfa/clipboard'

interface Props {
  /** null while POST /mfa/enroll/start is in flight or has failed. */
  enrollment: EnrollmentData | null
  loadingEnrollment: boolean
  /** POST /mfa/enroll/confirm is in flight. */
  submitting: boolean
  error: string | null
  errorSeq: number
  /** Called automatically as soon as 6 digits are entered (and on Enter). */
  onSubmitCode: (code: string) => void
  /** Retry POST /mfa/enroll/start after a failure. */
  onRetryStart: () => void
  onBack: () => void
}

/**
 * Forced first-login enrollment: QR code + manual key, then the first 6-digit code
 * to prove the authenticator app is set up correctly.
 */
export function MfaEnrollStep({
  enrollment,
  loadingEnrollment,
  submitting,
  error,
  errorSeq,
  onSubmitCode,
  onRetryStart,
  onBack,
}: Props) {
  const [value, setValue] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const lastAutoSubmitted = useRef<string | null>(null)

  useEffect(() => {
    if (errorSeq > 0) {
      setValue('')
      lastAutoSubmitted.current = null
    }
  }, [errorSeq])

  // Focus the code field on mount and whenever a request settles (the field is disabled while
  // a request is in flight, so focusing at that moment would be a no-op).
  useEffect(() => {
    if (!submitting) inputRef.current?.focus()
  }, [submitting])

  useEffect(() => {
    if (!copied) return
    const t = window.setTimeout(() => setCopied(false), 2000)
    return () => window.clearTimeout(t)
  }, [copied])

  const handleChange = (raw: string) => {
    const next = sanitizeTotpInput(raw)
    setValue(next)
    setLocalError(null)
    if (isCompleteTotp(next) && !submitting && lastAutoSubmitted.current !== next) {
      lastAutoSubmitted.current = next
      onSubmitCode(next)
    }
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (submitting || !enrollment) return
    if (!isCompleteTotp(value)) {
      setLocalError(`Enter the ${TOTP_LENGTH}-digit code shown in your authenticator app.`)
      return
    }
    lastAutoSubmitted.current = value
    onSubmitCode(value)
  }

  const handleCopyKey = async () => {
    if (!enrollment) return
    const ok = await copyToClipboard(enrollment.manualKey)
    setCopied(ok)
  }

  const shownError = localError || error
  const busy = submitting || loadingEnrollment

  return (
    <form className='form w-100' onSubmit={handleSubmit} noValidate id='kt_login_mfa_enroll_form'>
      {/* begin::Heading */}
      <div className='text-center mb-8'>
        <h1 className='text-dark fw-bolder mb-3'>Set up two-step verification</h1>
        <div className='text-gray-500 fw-semibold fs-6'>
          Two-step verification is now required for all admin accounts. Scan the QR code with
          Google Authenticator (or any authenticator app), then enter the 6-digit code it shows.
        </div>
      </div>
      {/* end::Heading */}

      {shownError && (
        <div className='mb-8 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{shownError}</div>
        </div>
      )}

      {/* begin::QR */}
      <div className='d-flex flex-column align-items-center mb-8'>
        {loadingEnrollment && (
          <div className='d-flex flex-column align-items-center text-gray-500 fw-semibold fs-6 py-10'>
            <span className='spinner-border spinner-border-lg text-primary mb-4' role='status' aria-hidden='true'></span>
            Preparing your QR code...
          </div>
        )}

        {!loadingEnrollment && !enrollment && (
          <div className='text-center py-6'>
            <div className='text-gray-700 fw-semibold fs-6 mb-4'>We could not prepare your QR code.</div>
            <button type='button' className='btn btn-light-primary' onClick={onRetryStart}>
              Try again
            </button>
          </div>
        )}

        {enrollment && (
          <>
            <div className='border border-gray-300 rounded p-3 bg-white mb-5'>
              <img
                src={enrollment.qrDataUrl}
                alt='QR code for your authenticator app'
                width={200}
                height={200}
                style={{display: 'block', imageRendering: 'pixelated'}}
              />
            </div>
            <div className='text-gray-500 fw-semibold fs-7 mb-2'>Can't scan it? Enter this key manually:</div>
            <div className='d-flex align-items-center gap-3 flex-wrap justify-content-center'>
              <code
                className='fs-5 fw-bold text-dark bg-light rounded px-4 py-2'
                style={{letterSpacing: '0.15em', wordBreak: 'break-all'}}
              >
                {enrollment.manualKey}
              </code>
              <button
                type='button'
                className={clsx('btn btn-sm', copied ? 'btn-light-success' : 'btn-light-primary')}
                onClick={handleCopyKey}
                disabled={busy}
              >
                {copied ? 'Copied' : 'Copy key'}
              </button>
            </div>
          </>
        )}
      </div>
      {/* end::QR */}

      {enrollment && (
        <>
          {/* begin::Form group */}
          <div className='fv-row mb-8'>
            <label className='form-label fs-6 fw-bolder text-dark' htmlFor='kt_login_mfa_enroll_code'>
              Code from your authenticator app
            </label>
            <input
              id='kt_login_mfa_enroll_code'
              ref={inputRef}
              type='text'
              inputMode='numeric'
              pattern='[0-9]*'
              autoComplete='one-time-code'
              maxLength={TOTP_LENGTH}
              placeholder='123456'
              value={value}
              disabled={busy}
              onChange={(e) => handleChange(e.target.value)}
              className={clsx('form-control bg-transparent text-center fs-2 fw-bold', {
                'is-invalid': !!shownError,
              })}
              style={{letterSpacing: '0.5em'}}
              autoFocus
            />
          </div>
          {/* end::Form group */}

          {/* begin::Action */}
          <div className='d-grid mb-8'>
            <button
              type='submit'
              id='kt_login_mfa_enroll_submit'
              className='btn btn-primary'
              disabled={busy || !isCompleteTotp(value)}
            >
              {!submitting && <span className='indicator-label'>Activate two-step verification</span>}
              {submitting && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
          {/* end::Action */}
        </>
      )}

      <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold'>
        <a
          href='#'
          className='link-primary'
          onClick={(e) => {
            e.preventDefault()
            if (!submitting) onBack()
          }}
        >
          Back to sign in
        </a>
      </div>
    </form>
  )
}
